import { makeAutoObservable, action } from "mobx";
import {
  IUser,
  ILines,
  IModules,
  IStation,
  IOrgs,
  IPlants,
  IDeviceSecretIssued,
  IDeviceSecretRevoked,
  IDeviceSecretStatus,
} from "../models/IUser";
import { ROLES, RoleType } from "../utils/roles";
import AuthService from "../services/AuthService";
import ProfileService from "../services/ProfileService";
import apiService from "../services/apiService";
import { forgetCsrfToken, rememberCsrfToken } from "../utils/csrf";
import { registrationErrorMessage } from "../utils/registration";
import { forgetReturnPath } from "../utils/returnPath";
import { logApiError } from "../utils/logApiError";
// `Н48`: тело административной правки — только поля белого списка сервера.
import { adminEditableFields } from "../utils/adminUserFields";

export class Store {
  // Auth state
  user = {} as IUser;
  isAuth = false;
  isLoading = false;
  isUpdate = true;
  error: string | null = null;
  authError: string | null = null;

  // Plants loading state
  isPlantsLoading = false;

  // Users state
  users: IUser[] = [];

  // Orgs state
  companies: IOrgs[] = [];
  plants: IPlants[] = [];
  company: IOrgs | null = null;
  plant: IPlants | null = null;

  // Lines state
  lines: ILines[] = [];
  modules: IModules[] = [];
  stations: IStation[] = [];
  line: ILines | null = null;
  module: IModules | null = null;
  station = {};

  // logs
  logs = {};
  firmWares = [];
  isPoll = true;

  private _devLogs: string = "";
  private _devLogsLoading: boolean = false;
  private _devLogsError: string | null = null;

  constructor() {
    makeAutoObservable(this);
    this.loadState();
  }

  // Auth methods

  @action
  setUpdate(isUpdate: boolean) {
    this.isUpdate = isUpdate;
  }

  @action
  setPoll(isPoll: boolean) {
    this.isPoll = isPoll;
  }
  @action
  setAuth(isAuth: boolean) {
    this.isAuth = isAuth;
  }

  @action
  setUser(user: IUser) {
    this.user = user;
  }

  @action
  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  @action
  setError(error: string | null) {
    this.error = error;
  }

  @action
  setAuthError(error: string | null) {
    this.authError = error;
  }

  @action
  setPlantsLoading(isPlantsLoading: boolean) {
    this.isPlantsLoading = isPlantsLoading;
  }

  @action
  updateItemInArray<T>(
    arrayName: "stations" | "modules" | "lines",
    idField: keyof T,
    id: any,
    updates: Partial<T>,
  ) {
    const array = this[arrayName] as T[];

    const item = array.find((el: any) => el[idField] === id);
    if (item) {
      Object.assign(item, updates);
    } else {
      console.warn(
        `[updateItemInArray] Элемент с ${String(idField)}=${id} не найден в ${arrayName}`,
      );
    }
  }

  async handleAuthSuccess(user: IUser) {
    // БЕЗ-01 (DEV-55): сессия живёт в httpOnly-cookie, которую ставит сервер;
    // в хранилище страницы токен не кладём и оттуда не читаем.
    this.isAuth = true;
    this.user = user;
    this.error = null;
    this.authError = null;

    const { role } = user;

    try {
      switch (role) {
        case ROLES.root:
          await this.getCompanies();
          await this.getAllPlants();
          break;

        case ROLES.org_oper:
          await this.getCompany(user.company_id);

          break;

        case ROLES.plant_oper:
          await this.getCompany(user.company_id);
          await this.getPlant(user.plant.id);
          break;

        case ROLES.station_owner:
          // station_owner не загружает компании/объекты, только свой профиль
          break;

        default:
          console.warn("[handleAuthSuccess] Unknown role:", role);
          break;
      }
    } catch (error) {
      // Общая ошибка — например, сеть, API недоступен
      logApiError("Failed to initialize user session", error);
      this.setError("Не удалось загрузить данные");
    }
  }

  async login(email: string, password: string) {
    this.setLoading(true);
    try {
      const response = await AuthService.login(email, password);

      // `АУД-Н156`: в консоли нет ни email, ни записи пользователя — журнал
      // браузера не место для персональных данных.
      console.info("[STORE] Login successful");

      rememberCsrfToken(response.data.csrfToken);
      this.handleAuthSuccess(response.data.user);
    } catch (error: any) {
      // `АУД-Н156`: email не пишем — в том числе и в теле ответа сервера.
      console.error("[STORE] Login failed", {
        error: error.message,
        status: error.response?.status,
      });

      this.setAuthError(error.response?.data?.message || "Login failed");
    } finally {
      this.setLoading(false);
    }
  }

  @action
  async registration(email: string, password: string) {
    this.setLoading(true);
    try {
      const response = await AuthService.registration(email, password);
      rememberCsrfToken(response.data?.csrfToken);
      this.setAuth(true);
      this.setUser(response.data.user);
      this.setAuthError(null);
    } catch (error: any) {
      // Старая одношаговая регистрация выключена: сервер отвечает 410, и
      // человеку нужен понятный текст, а не молчаливая ошибка (DEV-55).
      this.setAuthError(registrationErrorMessage(error));
    } finally {
      this.setLoading(false);
    }
  }

  @action
  async logout() {
    try {
      // 1. Уведомляем сервер
      await AuthService.logout(); // например, DELETE /auth/session

      console.info("[STORE] Logout successful");

      // 2. Только после успеха — очищаем клиент
      this.clearSessionData();

      // Можно дополнительно перенаправить:
      // window.location.href = '/login';
    } catch (error: any) {
      // Если сервер не ответил — не доверяем, не чистим полностью
      console.error("[STORE] Logout failed", {
        error: error.message,
        status: error.response?.status,
      });

      // Но даём возможность очистить локально (на случай, если токен скомпрометирован)
      this.setAuthError(error.message || "Не удалось выйти. Попробуйте снова.");

      // Админ может захотеть принудительно выйти:
      // this.forceLocalLogout();
    }
  }

  // Отдельный метод для сброса всех данных
  @action
  clearSessionData() {
    // БЕЗ-01: токен доступа стирает сервер (cookie); здесь — только сессионные
    // подсказки страницы.
    forgetCsrfToken();
    // `АУД-Н156`: путь возврата живёт в `sessionStorage` и переживал выход —
    // после следующего входа человека уводило туда, откуда он вышел. Выход
    // забывает и его.
    forgetReturnPath();
    localStorage.removeItem("authRedirectDone");

    // Сброс авторизации
    this.setAuth(false);
    this.setUser(null);
    this.setCompany(null);
    this.setPlant(null);

    // Сброс выбранных сущностей
    this.line = null;
    this.module = null;
    this.station = null;

    // Очистка кэшированных данных
    this.users = [];
    this.companies = [];
    this.plants = [];
    this.lines = [];
    this.modules = [];
    this.stations = [];
    this.firmWares = [];
    this.logs = {};
    this.error = null;

    // Опционально: сброс MobX state tree, если используешь MST
  }

  async checkAuth() {
    this.setLoading(true);
    try {
      const response = await AuthService.checkAuth();

      // `АУД-Н156`: в консоли нет ни email, ни записи пользователя.
      console.info("[STORE] Auth check successful");

      rememberCsrfToken(response.csrfToken);
      await this.handleAuthSuccess(response.user);

      // `0.7`: после п. 30 `/refresh` не отдаёт контакты — берём их отдельно.
      await this.loadMyProfile();
    } catch (error: any) {
      // Отсутствие сессии — обычное состояние анонимного посетителя, а не
      // ошибка входа: проверка идёт при каждой загрузке страницы.
      console.info("[STORE] Сессии нет", { status: error.response?.status });
      this.setAuth(false);
      this.setUser(null);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * `0.7`: контакты своего профиля — из `GET /my/profile`.
   *
   * Шапка и страница профиля читают `store.user.email` / `store.user.tel`;
   * заполняем их здесь, а не из ответа `/refresh`, который после п. 30 отдаёт
   * `null`. Сбой загрузки профиля сессию не отменяет: контакты — не признак
   * входа.
   */
  async loadMyProfile() {
    try {
      const profile = await ProfileService.getProfile();

      this.updateUser({
        id: String(profile.userId || this.user.id || ""),
        email: profile.email ?? this.user.email ?? "",
        tel: profile.tel ?? null,
        firstName: profile.firstName ?? null,
        lastName: profile.lastName ?? null,
        surName: profile.surName ?? null,
        // `Ф0`: плашки кабинета строятся по состоянию учётки и сроку удаления.
        accountState: profile.accountState ?? this.user.accountState ?? null,
        deletionDueAt: profile.deletionDueAt ?? null,
      });
    } catch (error) {
      logApiError("Failed to load my profile", error);
    }
  }

  // Users methods
  @action
  setUsers(users: IUser[]) {
    this.users = users;
  }

  @action
  updateUser(userData: Partial<IUser>) {
    this.user = { ...this.user, ...userData };
  }

  @action
  deleteUser(userId: number) {
    this.users = this.users.filter((user) => user.id !== userId);
  }

  async fetchUsers() {
    this.setLoading(true);
    try {
      const users = await apiService.fetchEntity("/users");
      this.setUsers(users.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch users");
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * `Н48`: у поля роли **одно** имя — `roleId`. Прежде форма слала `roleID`, а
   * схема сервера требовала `roleId`, — с сайта не заводился оператор.
   */
  async createUser(newUser: {
    email?: string;
    tel?: string;
    password?: string;
    roleId?: number;
    companyId?: number;
    plantId?: number;
    stationId?: number;
  }) {
    try {
      const response = await apiService.createEntity("/user", newUser);
      if (response.success) {
        this.updateUser(newUser);
      }
      this.fetchUsers();
    } catch (error: any) {
      this.setError(error.message || "Failed to create user");
      // `Н48.1` (`АУД-Н174`): отказ сервера пробрасываем вызывающему. Прежде
      // ошибка только оседала в сторе, `await` завершался успешно — и форма
      // закрывалась, не показав причину.
      throw error;
    }
  }

  async editUser(userUpdate: { tel?: string; email?: string }) {
    const ID = Number(this.user.id);
    try {
      const response = await apiService.patchEntity<{ success: boolean }>(
        "/user",
        ID,
        userUpdate,
      );
      if (response.data.success) {
        this.updateUser(userUpdate);
      }
    } catch (error: any) {
      this.setError(error.message || "Failed to edit user");
      // `Н48.1` (`АУД-Н174`): то же для правки своей записи — форма не закрывается
      // молча, причина уходит вызывающему.
      throw error;
    }
  }

  // В сторе
  async updateUserByAdmin(userData) {
    try {
      const response = await apiService.patchEntity("/users", userData.id, {
        ...adminEditableFields(userData),
        ...(userData.stationId !== undefined && {
          station_id: userData.stationId,
        }),
        ...(userData.detachStationIds && {
          detach_station_ids: userData.detachStationIds,
        }),
      });

      if (response.data.success) {
        // Обновить в списке
        const index = this.users.findIndex((u) => u.id === userData.id);
        if (index !== -1) {
          this.users[index] = { ...this.users[index], ...response.data.user };
        }

        // Обновить весь список
        await this.fetchUsers();
      }
    } catch (error: any) {
      this.setError(error.message);
      // `Н48.1` (`АУД-Н174`): правка администратором тоже не должна закрывать
      // форму молча — причина отказа уходит в форму.
      throw error;
    }
  }

  async DeleteUserByAdmin(id: number) {
    try {
      await apiService.deleteEntity("/users", id);
      this.deleteUser(id); // Удаляем из локального состояния
    } catch (error) {
      logApiError("Ошибка при удалении пользователя:", error);
      throw error;
    }
  }

  // Orgs methods
  @action
  setCompanies(companies: IOrgs[]) {
    this.companies = companies;
  }

  @action
  setPlants(plants: IPlants[]) {
    this.plants = plants;
  }

  @action
  setCompany(company: IOrgs | null) {
    console.log("set company");
    this.company = company;
    this.saveState();
  }

  @action
  setPlant(plant: IPlants | null) {
    this.plant = plant;
    this.saveState();
  }

  @action
  resetPlant() {
    this.plant = null;
    this.saveState();
  }

  @action
  updatePlantPhotoUrl(photoUrl: string) {
    if (this.plant) {
      this.plant.photoUrl = photoUrl;
      this.saveState();
    }
  }

  async getCompanies() {
    try {
      const companies = await apiService.fetchEntity("/orgs");
      this.setCompanies(companies.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch companies");
    }
  }
  async getCompany(id: string | number) {
    try {
      const response = await apiService.fetchEntity(`/org/${id}`);
      const companyData = response.data;

      if (!companyData) {
        throw new Error("Company not found");
      }

      // Просто кладём компанию в массив из одного элемента
      this.setCompanies([companyData]);

      // Делаем её активной
      this.setCompany(companyData);
    } catch (error: any) {
      logApiError("[getCompany] Ошибка:", error);
      this.setError(error.message || "Не удалось загрузить организацию");
    }
  }

  async createOrganization(data: Partial<IOrgs>) {
    try {
      const newOrg = await apiService.createEntity("/createOrg", data);
      console.log(newOrg.data, "orgData");
      this.companies.push(newOrg.data);
      return newOrg.data; // ← возвращаем
    } catch (error: any) {
      this.setError(error.message || "Failed to create organization");
    }
  }
  // refact
  async editOrganization(companyUpdate) {
    const ID = this.company?.id;
    try {
      await apiService.patchEntity<{ success: boolean }>(
        "/org",
        ID,
        companyUpdate,
      );
      this.getCompanies();
    } catch (error: any) {
      this.setError(error.message || "Failed to edit organization");
    }
  }

  /** `B.12`: новый ключ отдаётся один раз; карточка после этого — без ключа. */
  async reissueOrgApiKey(id: number): Promise<{ apiKey: string }> {
    const response = await apiService.POST<{ apiKey: string }>(
      `/org/${id}/api-key`,
      {},
    );

    await this.getCompanies();

    const updated = this.companies.find((company) => company.id === id);
    if (updated) this.setCompany(updated);

    return response.data;
  }

  /** `B.12`: после отзыва список перечитывается — «последнее обращение» сброшено. */
  async revokeOrgApiKey(id: number): Promise<void> {
    await apiService.DELETE<{ revoked: boolean }>(`/org/${id}/api-key`);

    await this.getCompanies();

    const updated = this.companies.find((company) => company.id === id);
    this.setCompany(updated ?? null);
  }

  async deleteCompany() {
    const ID = this.company.id;
    try {
      await apiService.deleteEntity("/org", ID);
      this.companies = this.companies.filter((company) => company.id !== ID);
    } catch (error: any) {
      this.setError(error.message || "Failed to delete company");
    }
  }

  async getPlant(id: string | number) {
    try {
      const response = await apiService.fetchEntity(`/plant/${id}`);

      // 🔥 ИСПРАВЛЕНО: извлекаем не response.data, а response.data.data
      const plantData = response.data.data;

      if (!plantData || !plantData.id) {
        console.error("❌ Не удалось извлечь данные объекта");
        throw new Error("Plant not found");
      }

      this.setPlants([plantData]);
      this.setPlant(plantData);
    } catch (error: any) {
      logApiError("[getPlant] Ошибка при загрузке объекта:", error);
      this.setError(error.message || "Не удалось загрузить объект");
    }
  }

  async getPlants(companyId: number) {
    this.setPlantsLoading(true);
    try {
      const plants = await apiService.fetchEntity(`/plants/${companyId}`);
      this.setPlants(plants.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch plants");
    } finally {
      this.setPlantsLoading(false);
    }
  }

  async getAllPlants() {
    this.setPlantsLoading(true);
    try {
      const plants = await apiService.fetchEntity("/plants");
      this.setPlants(plants.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch all plants");
    } finally {
      this.setPlantsLoading(false);
    }
  }

  async createPlant(data: Partial<IPlants>) {
    try {
      await apiService.createEntity("/createPlant", data);
      await this.refreshPlantsList(); // <-- делегируем логику обновления
    } catch (error: any) {
      this.setError(error.message || "Failed to create plant");
    }
  }

  async editPlant(data: Partial<IPlants>) {
    const ID = data.id;
    if (!ID) {
      this.setError("Plant ID is required for editing");
      return;
    }

    try {
      await apiService.patchEntity("/plant", ID, data);
      await this.refreshPlantsList(); // <-- делегируем логику обновления
    } catch (error: any) {
      this.setError(error.message || "Failed to edit plant");
    }
  }

  // Вспомогательный метод — обновляет список в зависимости от роли
  private async refreshPlantsList() {
    switch (this.user.role) {
      case "root":
        await this.getAllPlants();
        break;
      case "оператор организации":
        if (this.company?.id) {
          await this.getPlants(this.company.id);
        } else {
          console.warn("Company ID is missing for operator role");
          // Можно также вызвать setError, если это критично
          // this.setError("Company not set for operator");
        }
        break;
      default:
        // Можно добавить логику по умолчанию, например, ничего не делать
        // или тоже вызвать getAllPlants, если нужно
        console.warn(`Unhandled role: ${this.user.role}`);
        break;
    }
  }

  async deletePlant(ID: number) {
    try {
      await apiService.deleteEntity("/plant", ID);
      const newplants = this.plants.filter((plant) => plant.id !== ID);
      this.setPlants(newplants);
    } catch (error: any) {
      this.setError(error.message || "Failed to delete plant");
    }
  }
  //fetch for plant
  // store
  async loadPlantData() {
    const ID = this.plant?.id;
    if (!ID) return;

    this.setLoading(true);
    try {
      const data = await apiService.fetchEntity(`/plantData/${ID}`);

      this.setLines(
        [...data.data.lines].sort((a, b) => a.id - b.id), // или по id, как нужно
      );
      this.setModules(
        [...data.data.modules].sort((a, b) => a.id - b.id), // или по другому полю
      );
      this.setStations(
        [...data.data.stations].sort(
          (a, b) => a.controller_address - b.controller_address,
        ),
      );
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch plant data");
    } finally {
      this.setLoading(false);
    }
  }

  // 🚀 Метод для экспорта показаний
  // 🚀 Метод для экспорта показаний по диапазону дат
  async exportReadings(
    startDate: string,
    endDate: string,
    format: "excel" | "pdf" = "excel", // ← добавлен параметр
  ): Promise<void> {
    if (!this.plant?.id) {
      console.error("Plant ID is not available");
      throw new Error("Plant ID is not available");
    }

    try {
      // Отправляем данные + формат на бэк
      const blob = await apiService.exportFile("/export", {
        plant_id: this.plant.id,
        start_date: startDate,
        end_date: endDate,
        format, // ← передаём формат
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Форматируем даты для имени файла
      const formatForFilename = (isoDate: string): string => {
        const [year, month, day] = isoDate.split("-");
        return `${day}.${month}.${year}`;
      };

      const startDisplay = formatForFilename(startDate);
      const endDisplay = formatForFilename(endDate);

      // Определяем расширение по формату
      const ext = format === "pdf" ? "pdf" : "xlsx";

      a.download = `показания_${startDisplay}—${endDisplay}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logApiError("Ошибка при экспорте диапазона:", error);
      throw error;
    }
  }

  // Lines methods
  @action
  setLines(lines: ILines[]) {
    this.lines = lines;
  }

  @action
  setModules(modules: IModules[]) {
    this.modules = modules;
  }

  @action
  setLine(line: ILines | null) {
    this.line = line;
  }

  @action
  private pushLine(line: ILines) {
    this.lines = [...this.lines, line];
  }

  @action
  setModule(module: IModules | null) {
    this.module = module;
  }

  @action
  setStation(station: IStation) {
    this.station = station;
  }

  @action
  setStations(stations: IStation[]) {
    this.stations = stations;
  }

  async createLine(data: Partial<ILines>) {
    try {
      if (this.plant?.id) {
        data.plantId = this.plant.id;
      }
      const newLine = await apiService.createEntity("/createLine", data);
      this.pushLine(newLine.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to create line");
    }
  }

  async getLines() {
    const ID = this.plant?.id;

    if (!ID) {
      this.setError("Plant not selected - get lines");
      return;
    }
    try {
      const lines = await apiService.fetchEntity(`lines/${ID}`);
      this.setLines(lines.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch lines");
    }
  }
  //refact
  async updateLine(line: Partial<ILines>) {
    const ID = line.id;
    console.log(ID, "lineID");
    try {
      const response = await apiService.patchEntity("/line", ID, line); // ← сохраняем ответ
      await this.loadPlantData();
      return response; // ← возвращаем ответ вызывающему
    } catch (error: any) {
      this.setError(error.message || "Failed to update line");
      throw error; // ← пробрасываем ошибку, чтобы компонент мог её обработать
    }
  }

  async deleteLine(ID: number): Promise<void> {
    if (this.isLoading) return; // Защита от повторных вызовов

    this.isLoading = true;
    this.error = null;

    try {
      await apiService.deleteEntity("/line", ID);
      this.lines = this.lines.filter((line) => line.id !== ID);

      await this.getModules();
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete line";

      this.error = message;
      logApiError("Ошибка при удалении линии:", error);
    } finally {
      this.isLoading = false;
    }
  }

  async createModule(data: Partial<IModules>) {
    try {
      if (this.plant?.id) {
        data.plant_id = this.plant.id;
      }
      const response = await apiService.createEntity("/createModule", data);
      await this.getModules();
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create module";
      this.setError(errorMessage);
      throw new Error(errorMessage); // Важно: выбрасываем ошибку с сообщением
    }
  }

  async getModules() {
    const ID = this.plant.id;
    if (!ID) {
      this.setError("Plant not selected - get modules");
      return;
    }
    try {
      const modules = await apiService.fetchEntity(`/modules/${ID}`);
      this.setModules(modules.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch modules");
    }
  }

  async getOrgModules() {
    const ID = this.company.id;
    if (!ID) {
      this.setError("ORGANIZATION not selected");
      return;
    }
    try {
      const modules = await apiService.fetchEntity(`/orGmodules/${ID}`);
      this.setModules(modules.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch modules");
    }
  }

  async getAllModules() {
    try {
      const modules = await apiService.fetchEntity("/modules");
      this.setModules(modules.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch all modules");
    }
  }

  async updateModule(
    module: Partial<IModules>,
    stations: Partial<IStation>[],
    childModules: Partial<IModules>[],
    modifiedKeys: string[],
  ) {
    // 🔍 Проверка: изменился ли ТОЛЬКО timer?
    const isTimerOnly =
      Array.isArray(modifiedKeys) &&
      modifiedKeys.length === 1 &&
      modifiedKeys[0] === "timer" &&
      module.timer !== undefined;

    if (isTimerOnly) {
      const res = await this.updateTimer(module.charge_id!, module.timer); // или module.charge_id, как у тебя
      return { data: { isTimer: true, ...res } };
    }

    const patchedStations = stations.map((s) => {
      const station = { ...s };
      if ("Meters.meter_address" in station) {
        station.meter_address = station["Meters.meter_address"];
        delete station["Meters.meter_address"];
      }
      if ("Meters.meter_number" in station) {
        station.meter_number = station["Meters.meter_number"];
        delete station["Meters.meter_number"];
      }
      return station;
    });

    const payload = { module, stations: patchedStations, childModules, modifiedKeys };
    try {
      const response = await apiService.patchEntity<IModules>(
        "/module",
        module.id!,
        payload,
      );
      return { isTimer: false, ...response }; // ← важно: ...response.data
    } catch (error: any) {
      this.setError(error.message || "Неизвестная ошибка");
      logApiError("Ошибка при обновлении модуля:", error);
      throw error;
    }
  }

  /**
   * Отдельный метод для сохранения изменений СМБ (дочерних) модулей
   * из таблицы в popupModule. Отправляет собственный запрос на бэкенд.
   */
  async updateChildModules(
    parentModule: Partial<IModules>,
    childModules: Partial<IModules>[],
    modifiedKeys: string[] = [],
  ) {
    const parentId = parentModule.id;
    if (!parentId) {
      throw new Error("Parent module ID is required");
    }

    try {
      // Бэкенд ожидает PATCH /mmb_module/:parentId
      // с payload { parentModule, childModules, modifiedKeys }
      const response = await apiService.patchEntity<IModules>(
        "/mmb_module",
        parentId,
        {
          parentModule,
          childModules,
          modifiedKeys,
        },
      );

      if (this.plant?.id) {
        await this.loadPlantData();
      }

      return response;
    } catch (error: any) {
      this.setError(error.message || "Неизвестная ошибка");
      logApiError("Ошибка при обновлении дочерних модулей:", error);
      throw error;
    }
  }

  /**
   * Сохранение изменений модуля типа СМБ.
   * Бэкенд ожидает PATCH /smb_module/:id
   * с payload { module, stations, modifiedKeys }
   */
  async updateSmbModules(
    module: Partial<IModules>,
    stations: Partial<IStation>[],
    modifiedKeys: string[] = [],
  ) {
    // 🔍 Проверка: изменился ли ТОЛЬКО timer?
    const isTimerOnly =
      Array.isArray(modifiedKeys) &&
      modifiedKeys.length === 1 &&
      modifiedKeys[0] === "timer" &&
      module.timer !== undefined;

    if (isTimerOnly) {
      const res = await this.updateTimer(module.charge_id!, module.timer);
      return { data: { isTimer: true, ...res } };
    }

    const patchedStations = stations.map((s) => {
      const station = { ...s };
      if ("Meters.meter_address" in station) {
        station.meter_address = station["Meters.meter_address"];
        delete station["Meters.meter_address"];
      }
      if ("Meters.meter_number" in station) {
        station.meter_number = station["Meters.meter_number"];
        delete station["Meters.meter_number"];
      }
      return station;
    });

    const payload = { module, stations: patchedStations, modifiedKeys };
    try {
      const response = await apiService.patchEntity<IModules>(
        "/smb_module",
        module.id!,
        payload,
      );
      return { isTimer: false, ...response };
    } catch (error: any) {
      this.setError(error.message || "Неизвестная ошибка");
      logApiError("Ошибка при обновлении СМБ модуля:", error);
      throw error;
    }
  }

  // Исправленный updateTimer
  async updateTimer(identifier: string, timerValue: number) {
    try {
      const response = await apiService.POST("/set-timer", {
        identifier, // ← ✅ именно deviceId, как на бэке
        timer: timerValue,
      });

      // Возвращаем ТОЛЬКО данные, которые нужны компоненту
      return {
        success: response.data.success,
        status: response.data.status, // будет "accepted" или "rejected"
        // message можно добавить, если нужно
      };
    } catch (error: any) {
      throw new Error(error.message || "Ошибка обновления таймера");
    }
  }

  async deleteModule(id: number) {
    try {
      await apiService.deleteEntity<ApiResponse<any>>("/modules", id, {
        params: {
          force: true,
          reason: "admin",
        },
      });
      this.modules = this.modules.filter((module) => module.id !== id);
    } catch (error: any) {
      this.setError(error.message || "Failed to delete module");
    }
  }

  async restartModule(identifier: string) {
    try {
      const response = await apiService.POST<{
        message: string;
        error?: string;
        status?: string;
      }>(`/restartModule`, { identifier });

      // 🔴 Проверяем: есть ли ошибка в теле ответа?
      if (response.data?.error) {
        // Создаём объект ошибки, как будто она пришла с 4xx/5xx
        const error = new Error(response.data.error);
        (error as any).response = { data: response.data, status: 400 };
        throw error;
      }

      return response.data;
    } catch (error: any) {
      logApiError("Restart failed", error);

      // Пробрасываем ошибку дальше, чтобы компонент мог её обработать
      throw error;
    }
  }

  // DEV-78 (ПРВ-06, ПРВ-05): операции с секретом изделия. Значение секрета
  // возвращает только выдача и показывается один раз; признак выдачи значения не
  // содержит. Ошибки пробрасываем наверх — сообщение показывает карточка.
  async fetchDeviceSecretStatus(moduleId: number): Promise<IDeviceSecretStatus> {
    const response = await apiService.fetchEntity<IDeviceSecretStatus>(
      `/module/${moduleId}/device-secret`,
    );
    return response.data;
  }

  /**
   * `п. 1` резерва (пара к п. 19): выдача секрета.
   *
   * `replace: true` — **осознанный перевыпуск**: сервер отвечает `409`, если
   * секрет уже выдан, а замену не назвали. Тело без `replace` — первая выдача,
   * она ничего не обесценивает и подтверждения не требует.
   */
  async issueDeviceSecret(
    moduleId: number,
    options: { replace?: boolean } = {},
  ): Promise<IDeviceSecretIssued> {
    const response = await apiService.POST<IDeviceSecretIssued>(
      `/module/${moduleId}/device-secret`,
      // Первая выдача уходит с пустым телом: `replace` в теле означает названную
      // замену, и подставлять его «на всякий случай» нельзя.
      options.replace === true ? { replace: true } : {},
    );
    return response.data;
  }

  async revokeDeviceSecret(moduleId: number): Promise<IDeviceSecretRevoked> {
    const response = await apiService.POST<IDeviceSecretRevoked>(
      `/module/${moduleId}/device-secret/revoke`,
      {},
    );
    return response.data;
  }

  // `Б4`: внесение пароля точки доступа из паспорта изделия. Изделию команда не
  // уходит — пароль уже стоит в паспорте; сервер принимает его только при
  // `state = none`, иначе отвечает 409 «смена через rotate».
  // `Б2.2`: чтение пароля точки — только по нажатию: каждое чтение пишется в
  // журнал действий, поэтому карточка при загрузке его не зовёт.
  async fetchApPassword(moduleId: number) {
    const response = await apiService.fetchEntity<{
      apPassword: string | null;
      state: string;
      changedAt: string | null;
    }>(`/module/${moduleId}/ap-password`);
    return response.data;
  }

  // `Б2.2`: смена пароля — новый уходит изделию, действующий остаётся до
  // подтверждения; состояние в ответе решает, что показать в карточке.
  async rotateApPassword(moduleId: number) {
    const response = await apiService.POST<{
      state: string;
      changedAt: string | null;
    }>(`/module/${moduleId}/ap-password/rotate`, {});
    return response.data;
  }

  async importApPassword(moduleId: number, apPassword: string) {
    const response = await apiService.POST<{
      state: string;
      changedAt: string | null;
    }>(`/module/${moduleId}/ap-password/import`, { apPassword });
    return response.data;
  }

  async ignoreModule(id: number) {
    try {
      const response = await apiService.POST<{
        success: boolean;
        message: string;
      }>(`/ignoreModule`, { id });

      return response.data; // ⬅ обязательно верни, чтобы компонент мог прочитать результат
    } catch (error: any) {
      logApiError("Failed to toggle ignore:", error);
      // Передадим "ошибку" как объект, понятный компоненту
      return {
        success: false,
        message: "Не удалось изменить режим игнорирования",
      };
    }
  }

  async FirmwareUpdate(id: string) {
    try {
      const response = await apiService.fetchEntity(`/modulesFirmware/${id}`);
      return response.data;
    } catch (error) {}
  }
  //_______________________________________________________________________________________
  async getStations() {
    const ID = this.plant?.id;
    if (!this.plant?.id) {
      this.setError("Plant not selected - get stations");
      return;
    }

    try {
      const stations = await apiService.fetchEntity(`/stations/${ID}`);
      this.setStations(stations.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch stations");
    }
  }

  async getOrgStations() {
    if (!this.company?.id) {
      this.setError("Company not selected");
      return;
    }
    const ID = this.company.id;

    try {
      const stations = await apiService.fetchEntity(`/orGstations/${ID}`);
      this.setStations(stations.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch stations");
    }
  }

  async getAllStations() {
    try {
      const stations = await apiService.fetchEntity("/allStations");
      this.setStations(stations.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch all stations");
    }
  }

  // фронт: ваш метод updateStation
  async updateStation(station: Partial<IStation>, position: StationPosition) {
    const ID = station.id;

    try {
      const response = await apiService.patchEntity("/station", ID, station);

      // ✅ В зависимости от position — вызываем нужный метод
      if (position === "list") {
        await this.getAllStations(); // перезагружаем список станций
      } else if (position === "inplants") {
        await this.loadPlantData(); // перезагружаем данные завода
      }

      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Не удалось обновить станцию";
      this.setError(errorMessage);
      throw error; // ← важно! чтобы catch в компоненте сработал
    }
  }

  // store.ts
  async restartStation(id: number, controller: number) {
    try {
      const response = await apiService.POST("/restart", { id, controller });
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error ||
        error.message ||
        "Не удалось перезагрузить станцию";
      this.setError(message);
      throw new Error(message); // пробрасываем для обработки в компоненте
    }
  }

  // store. mobx
  async AvailabilityStation(
    id: number,
    moduleId: number,
    controller: number,
    availability: boolean,
  ) {
    try {
      const response = await apiService.POST("/availability", {
        id,
        moduleId,
        controller,
        availability,
      });

      // Успешный ответ от бэка — возвращаем данные
      return response.data;
    } catch (error: any) {
      // Извлекаем понятное сообщение об ошибке
      const message =
        error.response?.data?.error ||
        error.message ||
        "Не удалось изменить доступность станции";
      this.setError(message);
      throw new Error(message); // ← пробрасываем, чтобы компонент мог отреагировать
    }
  }

  async UnlockConnector(id: number, controller: number) {
    try {
      const response = await apiService.POST("/unlockconnector", {
        id,
        controller,
      });
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error ||
        error.message ||
        "Не удалось разблокировать разъём";
      this.setError(message);
      throw new Error(message); // чтобы компонент поймал ошибку
    }
  }

  // logs
  @action
  setLogs(logs: []) {
    this.logs = logs;
  }

  // В вашем MobX store
  async getLogs(limit: number, chargeId?: string) {
    // chargeId теперь опциональный параметр
    try {
      // Формируем строку запроса, добавляя chargeId, если он передан
      const params = new URLSearchParams();
      params.append("limit", limit.toString());
      if (chargeId) {
        params.append("chargeId", chargeId);
      }

      const queryString = params.toString();
      const logs = await apiService.fetchEntity(`/DBlogs?${queryString}`);
      return logs.data;
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch logs");
    }
  }
  //__________________________________________
  // firmware

  @action
  setFirmWares(firmWares: []) {
    this.firmWares = firmWares;
  }

  async fetchFirmWare() {
    try {
      const versions = await apiService.fetchEntity(`/firmware`);
      this.setFirmWares(versions.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch logs");
    }
  }

  async updateFirmWare(F: number, M: string, type: string) {
    try {
      const response = await apiService.POST(`/firmware`, { F, M, type });
      return response.data.message;
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch logs");
    }
  }

  async pollFirmLogs(moduleId: string, since: string) {
    try {
      const response = await apiService.fetchEntity(
        `/pollFirmWare/${moduleId}`,
        {
          params: { since },
        },
      );
      return response.data; // массив логов
    } catch (error) {
      this.setError(error.message || "Ошибка при получении логов");
      throw error; // чтобы в компоненте можно было перехватить
    }
  }

  //логи

  get devLogs(): string {
    return this._devLogs;
  }

  get devLogsLoading(): boolean {
    return this._devLogsLoading;
  }

  get devLogsError(): string | null {
    return this._devLogsError;
  }

  @action
  async fetchDevLogs(): Promise<void> {
    this._devLogsLoading = true;
    this._devLogsError = null;
    try {
      const response = await apiService.fetchEntity<string>("/admin/logs", {
        responseType: "text", // ← важно! Чтобы Axios не пытался парсить JSON
      });
      this._devLogs = response.data;
    } catch (err: any) {
      this._devLogsError =
        err.response?.data || err.message || "Неизвестная ошибка";
      this._devLogs = "";
    } finally {
      this._devLogsLoading = false;
    }
  }

  @action
  async clearDevLogs(): Promise<boolean> {
    try {
      await apiService.POST("/admin/logs/clear", {});
      this._devLogs = "";
      return true;
    } catch (err: any) {
      this._devLogsError =
        err.response?.data || err.message || "Не удалось очистить логи";
      return false;
    }
  }

  // LocalStorage methods

  saveState() {
    localStorage.setItem(
      "storeState",
      JSON.stringify({
        company: this.company,
        plant: this.plant,
      }),
    );
  }

  loadState() {
    const savedState = localStorage.getItem("storeState");
    if (savedState) {
      const { company, plant } = JSON.parse(savedState);
      this.company = company || null;
      this.plant = plant || null;
    }
  }
}

export const store = new Store();
